import qs from 'qs';
import { get, some } from 'lodash';

import { PUBLIC_PATHS } from 'utils/authorization/authConstants';
import { getMetaContent } from 'components/utils/html';
import { isAlternativePortalDomain } from 'utils/checkEnvironment';

export const isPublicPath = ({ pathname }) => pathname.startsWith('/public');

export const isSharedListPath = ({ pathname }) => pathname.startsWith('/sl/');

export const isShortListPath = ({ pathname }) =>
  pathname.startsWith('/shortlists');

export const isCompanyPortalPath = ({ pathname }) =>
  pathname.startsWith('/cp/');

export const isPublicProfilePath = ({ pathname }) => pathname.startsWith('/p/');

export const isPublicJobBoardPath = ({ pathname }) =>
  isJobBoardDomainPage() ||
  pathname === '/jobs' ||
  pathname.startsWith('/jobs/');

export const isLoginOrSignupPath = ({ pathname }) =>
  pathname.startsWith('/login') || pathname.startsWith('/signup');

export const isLoginPath = ({ pathname }) => pathname.startsWith('/login');

export const isLogoutPath = ({ pathname }) => pathname.startsWith('/logout');

const redirectParam = ({ search }) => {
  const redirect = get(
    qs.parse(search, { ignoreQueryPrefix: true }),
    'redirect',
    ''
  );

  return typeof redirect === 'string' ? redirect : '';
};

export const isSimpleUserLoginPath = (location) => {
  const redirect = redirectParam(location);

  return (
    isLoggingInToken(location) &&
    (isCustomDomainPage() ||
      isAlternativePortalDomain() ||
      isSharedListPath({ pathname: redirect }) ||
      isShortListPath({ pathname: redirect }) ||
      isCompanyPortalPath({ pathname: redirect }) ||
      isPublicJobBoardPath({ pathname: redirect }))
  );
};

export const isPublicJobBoardContext = (location) =>
  isPublicJobBoardPath(location) ||
  isPublicJobBoardPath({ pathname: redirectParam(location) });

export const isPublicPortalPath = (location) => {
  const isSimpleLoginPath = isSimpleUserLoginPath(location);

  return (
    isSharedListPath(location) ||
    isShortListPath(location) ||
    isCompanyPortalPath(location) ||
    isCustomDomainPath(location) ||
    isLoggingOut(location) ||
    isSimpleLoginPath
  );
};

export const isCustomDomainPath = ({ pathname }) =>
  (pathname === '/' ||
    pathname.startsWith('/requests') ||
    pathname.match(/^\/[a-zA-Z0-9]{22}/)) &&
  isCustomDomainPage();

export const isUnauthorizedPath = ({ pathname }) =>
  some(
    PUBLIC_PATHS,
    (path) => pathname === path || pathname.match(new RegExp(path))
  );

export const isSettingPassword = (location) => {
  const { pathname, query: { redirect } = {} } = location;
  return (
    pathname.startsWith('/setpassword') ||
    pathname.startsWith('/resetpassword') ||
    pathname.startsWith('/forgotpassword') ||
    (redirect && redirect.startsWith('/setpassword'))
  );
};

export const isOnboarding = ({ pathname }) =>
  pathname.startsWith('/onboarding');

export const isConfirmEmail = ({ pathname }) =>
  pathname.startsWith('/confirm-email');

export const isLoggingOut = ({ pathname }) => pathname.startsWith('/logout');

export const isLoggingInToken = ({ pathname, search }) =>
  pathname === '/login' &&
  search.match(
    /[?&](magic_link_token|login_token|email_confirm_token|password_reset_token|token)=/
  );

export const logoutRedirect = (preserveUrl = true) => {
  const path = `${window.location.pathname}${window.location.search}`;

  if (isPublicPortalPath(window.location)) {
    const redirect = get(
      qs.parse(window.location.search, { ignoreQueryPrefix: true }),
      'redirect',
      isLoggingInToken(window.location) ? '/' : path
    );

    return `/logout?expired=true&redirect=${redirect}`;
  }
  if (isLoggingOut(window.location)) {
    return `/logout${window.location.search}`;
  }
  return preserveUrl && !isLoggingInToken(window.location)
    ? `/logout?redirect=${encodeURIComponent(path)}`
    : '/logout';
};

export const isSharedListCandidateModalPath = ({ pathname }) => {
  const pathToMatch = new RegExp(
    '^/(shared|submission|marketing)-lists/\\w*/(candidates|messages)/\\w*(/(request|share)/\\w*$|$)'
  );

  return pathname.match(pathToMatch) !== null;
};

export const isPublicProfileCandidatePath = ({ location }) => {
  const portalPathWithoutCompany = new RegExp('/\\w+/candidate/\\w+$');

  return (
    isPublicPortalPath(location) &&
    portalPathWithoutCompany.test(location.pathname)
  );
};

export const isWithSidebarPath = (location) =>
  !isSettingPassword(location) &&
  !isConfirmEmail(location) &&
  !isPublicPath(location) &&
  !isPublicPortalPath(location) &&
  !isLoggingOut(location) &&
  !isOnboarding(location) &&
  !isPublicProfilePath(location) &&
  !isPublicJobBoardPath(location);

export const isWithSubMenuPath = ({ pathname }) =>
  !pathname.startsWith('/shared-lists');

export const isFluidLayoutPath = (location) =>
  location.pathname.startsWith('/integrations/bullhorn/widget') ||
  /^\/job-boards\/(new|[^/]+\/edit)$/.test(location.pathname) ||
  isPublicJobBoardPath(location);

export const isSharedListsAllowedPath = (location, excludedPaths = []) => {
  const { pathname } = location;

  return (
    !excludedPaths.some((excludedPath) => pathname.startsWith(excludedPath)) &&
    (pathname.startsWith('/shared-lists') ||
      pathname.startsWith('/marketing-lists') ||
      pathname.startsWith('/submission-lists') ||
      pathname.startsWith('/clients') ||
      pathname.startsWith('/reporting') ||
      pathname.startsWith('/test-reporting') ||
      pathname.startsWith('/messages') ||
      pathname.startsWith('/candidates') ||
      pathname.startsWith('/job-boards') ||
      pathname.startsWith('/jobs') ||
      pathname.startsWith('/settings') ||
      pathname.startsWith('/user') ||
      pathname.startsWith('/account') ||
      pathname.startsWith('/subscription-plan') ||
      pathname.startsWith('/integrations/bullhorn/shared-lists') ||
      pathname.startsWith('/integrations/bullhorn/clients') ||
      pathname.startsWith('/integrations/bullhorn/contacts') ||
      pathname.startsWith('/integrations/bullhorn/candidates') ||
      pathname.startsWith('/integrations/bullhorn/jobs') ||
      pathname.startsWith('/integrations/bullhorn/widget') ||
      pathname.startsWith('/integrations/salesforce/candidates') ||
      pathname.startsWith('/integrations/searcher_express/profile') ||
      isLoggingOut(location) ||
      isSettingPassword(location) ||
      isConfirmEmail(location) ||
      isPublicPath(location) ||
      isPublicPortalPath(location))
  );
};

export const isJobsAllowedPath = (company) =>
  !!get(company, 'job_board_enabled', false);

export const isCustomDomainPage = () =>
  !!getMetaContent("name='gustav:custom-domain-company-id'");

export const isJobBoardDomainPage = () =>
  !!getMetaContent("name='gustav:job-board-slug'");
